import api from '@/plugins/api'
import { unwrap } from '@/shared/utils/unwrap'
import type { PlatformUser } from '@/shared/types/models'
import type { ApiResponse } from '@/shared/types/api'

export type TwoFactorMethod = 'totp' | 'email'

export interface LoginResponse {
  user?: PlatformUser
  requires_2fa?: boolean
  /** Methode 2FA exigee : 'totp' (app) ou 'email' (OTP par email auto-envoye). */
  method?: TwoFactorMethod
  challenge_token?: string
  expires_in?: number
}

export interface Verify2FAResponse {
  user: PlatformUser
  used_backup_code: boolean
  remaining_backup_codes: number
}

export interface Setup2FAResponse {
  secret: string
  provisioning_uri: string
  qr_code: string
}

export interface SetupEmail2FAResponse {
  detail: string
  expires_in_minutes: number
}

export interface Enable2FAResponse {
  detail: string
  backup_codes: string[]
}

export interface SendOtpResponse {
  detail: string
  expires_in_minutes: number
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/api/admin/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

export async function activate(token: string, password: string, confirmPassword: string): Promise<PlatformUser> {
  const resp = await api.post('/api/admin/auth/activate', {
    token,
    password,
    confirm_password: confirmPassword,
  })
  return unwrap<{ user: PlatformUser }>(resp).user
}

export async function resendActivation(email: string): Promise<{ detail: string }> {
  const resp = await api.post('/api/admin/auth/resend-activation', { email })
  return unwrap<{ detail: string }>(resp)
}

export async function forgotPassword(email: string): Promise<{ detail: string }> {
  const resp = await api.post('/api/admin/auth/forgot-password', { email })
  return unwrap<{ detail: string }>(resp)
}

export async function resetPassword(token: string, password: string, confirmPassword: string): Promise<void> {
  await api.post('/api/admin/auth/reset-password', {
    token,
    password,
    confirm_password: confirmPassword,
  })
}

export async function setup2FA(): Promise<Setup2FAResponse> {
  const resp = await api.post('/api/admin/auth/2fa/setup', {})
  return unwrap<Setup2FAResponse>(resp)
}

export async function enable2FA(code: string): Promise<Enable2FAResponse> {
  const resp = await api.post('/api/admin/auth/2fa/enable', { code })
  return unwrap<Enable2FAResponse>(resp)
}

export async function disable2FA(password: string, code: string): Promise<void> {
  await api.post('/api/admin/auth/2fa/disable', { password, code })
}

export async function verify2FA(challengeToken: string, code: string): Promise<Verify2FAResponse> {
  const resp = await api.post('/api/admin/auth/2fa/verify', {
    challenge_token: challengeToken,
    code,
  })
  return unwrap<Verify2FAResponse>(resp)
}

// ── 2FA Email-OTP ─────────────────────────────────────────────────────

/** Initialise le mode email-OTP : envoie le 1er code par mail. */
export async function setupEmail2FA(): Promise<SetupEmail2FAResponse> {
  const resp = await api.post('/api/admin/auth/2fa/setup-email', {})
  return unwrap<SetupEmail2FAResponse>(resp)
}

/** Confirme le code OTP recu par mail et active le mode email. */
export async function enableEmail2FA(code: string): Promise<Enable2FAResponse> {
  const resp = await api.post('/api/admin/auth/2fa/enable-email', { code })
  return unwrap<Enable2FAResponse>(resp)
}

/**
 * (Re)envoie un OTP email.
 * Deux modes :
 *  - Authentifie (challengeToken omis) : pour retry setup / disable
 *  - Login (challengeToken fourni) : renvoie l'OTP a l'utilisateur du challenge
 */
export async function sendOtp(challengeToken?: string): Promise<SendOtpResponse> {
  const body = challengeToken ? { challenge_token: challengeToken } : {}
  const resp = await api.post('/api/admin/auth/2fa/send-otp', body)
  return unwrap<SendOtpResponse>(resp)
}

export interface WsTokenResponse {
  token: string
  expires_in: number
}

export async function getWsToken(): Promise<WsTokenResponse> {
  const resp = await api.post('/api/admin/auth/ws-token', {})
  return unwrap<WsTokenResponse>(resp)
}

export interface UpdateMePayload {
  first_name?: string
  last_name?: string
}

/**
 * Modifie son propre profil (prénom/nom uniquement).
 * Auth IsPlatformUser — accessible meme aux read_only. Tout autre champ
 * (email, is_owner, role…) est ignoré silencieusement cote backend.
 */
export async function updateMe(payload: UpdateMePayload): Promise<PlatformUser> {
  const resp = await api.patch<ApiResponse<{ user: PlatformUser; permissions?: string[] }>>(
    '/api/admin/auth/me', payload,
  )
  return resp.data.data.user
}
